import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, RefreshCw, Trophy, AlertTriangle } from "lucide-react";
import { Navigation } from "../components/sections/Navigation";
import { Footer } from "../components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FKF_URL = "https://www.footballkenya.org/sportpesa-premier-league";

const formatStamp = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (err) {
    return iso;
  }
};

const HeaderCell = ({ children, hideOnMobile, align = "center", testId }) => (
  <th
    data-testid={testId}
    className={`px-2 sm:px-3 py-3 text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-[#0A0A0A]/55 ${align === "left" ? "text-left" : "text-center"} ${hideOnMobile ? "hidden md:table-cell" : ""}`}
  >
    {children}
  </th>
);

const Cell = ({ children, hideOnMobile, align = "center", strong = false, accent = false }) => (
  <td
    className={`px-2 sm:px-3 py-4 text-sm ${align === "left" ? "text-left" : "text-center"} ${hideOnMobile ? "hidden md:table-cell" : ""} ${strong ? "font-bold text-[#0A0A0A]" : "text-[#0A0A0A]/75"} ${accent ? "text-[#D1111A]" : ""}`}
  >
    {children}
  </td>
);

// Qualification / relegation zones for an 18-team SPL season.
// Champion → CAF Champions League. 2nd → CAF Confederation Cup. Bottom 3 → relegation to NSL.
const getZone = (position, total) => {
  if (position === 1) {
    return {
      key: "champions",
      label: "Champions · CAF Champions League",
      shortLabel: "Champions",
      tone: "green",
      bar: "bg-[#15803D]",
      bg: "bg-[#15803D]/10 hover:bg-[#15803D]/15",
      pill: "bg-[#15803D] text-white",
    };
  }
  if (position === 2) {
    return {
      key: "confederation",
      label: "CAF Confederation Cup",
      shortLabel: "Confederation Cup",
      tone: "yellow",
      bar: "bg-[#EAB308]",
      bg: "bg-[#EAB308]/12 hover:bg-[#EAB308]/18",
      pill: "bg-[#EAB308] text-[#0A0A0A]",
    };
  }
  if (position > total - 3) {
    return {
      key: "relegation",
      label: "Relegation · NSL",
      shortLabel: "Relegation",
      tone: "red",
      bar: "bg-[#DC2626]",
      bg: "bg-[#DC2626]/8 hover:bg-[#DC2626]/12",
      pill: "bg-[#DC2626] text-white",
    };
  }
  return null;
};

const StandingsTable = ({ rows }) => {
  const total = rows.length;
  return (
    <div className="overflow-x-auto" data-testid="league-table-wrapper">
      <table className="w-full border-collapse" data-testid="league-table">
        <thead>
          <tr className="border-b-2 border-[#0A0A0A]">
            <th className="w-1.5 p-0" aria-hidden="true" />
            <HeaderCell testId="th-position">#</HeaderCell>
            <HeaderCell align="left" testId="th-club">Club</HeaderCell>
            <HeaderCell testId="th-played">P</HeaderCell>
            <HeaderCell hideOnMobile testId="th-won">W</HeaderCell>
            <HeaderCell hideOnMobile testId="th-drawn">D</HeaderCell>
            <HeaderCell hideOnMobile testId="th-lost">L</HeaderCell>
            <HeaderCell hideOnMobile testId="th-gf">GF</HeaderCell>
            <HeaderCell hideOnMobile testId="th-ga">GA</HeaderCell>
            <HeaderCell testId="th-gd">GD</HeaderCell>
            <HeaderCell testId="th-points">Pts</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((team, idx) => {
            const zone = getZone(team.position, total);
            const rowBg = zone ? zone.bg : "hover:bg-[#F4F4F5]";
            return (
              <motion.tr
                key={team.team_name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.4) }}
                className={`border-b border-[#E4E4E7] transition-colors ${rowBg}`}
                data-testid={`league-row-${team.position}`}
                data-zone={zone ? zone.key : "mid"}
              >
                <td
                  className={`p-0 w-1.5 ${zone ? zone.bar : "bg-transparent"}`}
                  aria-hidden="true"
                  data-testid={`league-row-bar-${team.position}`}
                />
                <Cell strong>
                  <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold">
                    {team.position}
                  </span>
                </Cell>
                <td className="px-2 sm:px-3 py-4 text-left">
                  <div className="flex items-center gap-3">
                    {team.logo_url ? (
                      <img
                        src={team.logo_url}
                        alt={`${team.team_name} crest`}
                        className="w-7 h-7 object-contain"
                        data-testid={`league-row-logo-${team.position}`}
                      />
                    ) : (
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A0A0A]/5 text-[10px] font-bold uppercase text-[#0A0A0A]/55">
                        {team.short_name || team.team_name.slice(0, 2)}
                      </span>
                    )}
                    <span className="font-semibold text-[#0A0A0A] text-sm sm:text-base" data-testid={`league-row-name-${team.position}`}>
                      {team.team_name}
                    </span>
                  </div>
                </td>
                <Cell strong>{team.played}</Cell>
                <Cell hideOnMobile>{team.won}</Cell>
                <Cell hideOnMobile>{team.drawn}</Cell>
                <Cell hideOnMobile>{team.lost}</Cell>
                <Cell hideOnMobile>{team.goals_for}</Cell>
                <Cell hideOnMobile>{team.goals_against}</Cell>
                <Cell strong>{team.goal_difference > 0 ? `+${team.goal_difference}` : team.goal_difference}</Cell>
                <Cell strong accent>{team.points}</Cell>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ZoneLegend = () => (
  <div
    className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
    data-testid="league-table-legend"
  >
    <div
      className="flex items-start gap-3 p-4 border-l-4 border-[#15803D] bg-[#15803D]/5"
      data-testid="legend-champions"
    >
      <span className="mt-1 inline-block w-3 h-3 bg-[#15803D]" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#15803D]">
          Champions
        </p>
        <p className="text-xs text-[#0A0A0A]/75 mt-1 leading-relaxed">
          Position 1 — Crowned SPL Champions and qualify for the CAF Champions League.
        </p>
      </div>
    </div>
    <div
      className="flex items-start gap-3 p-4 border-l-4 border-[#EAB308] bg-[#EAB308]/10"
      data-testid="legend-confederation"
    >
      <span className="mt-1 inline-block w-3 h-3 bg-[#EAB308]" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A16207]">
          Confederation Cup
        </p>
        <p className="text-xs text-[#0A0A0A]/75 mt-1 leading-relaxed">
          Position 2 — Qualifies for the CAF Confederation Cup.
        </p>
      </div>
    </div>
    <div
      className="flex items-start gap-3 p-4 border-l-4 border-[#DC2626] bg-[#DC2626]/5"
      data-testid="legend-relegation"
    >
      <span className="mt-1 inline-block w-3 h-3 bg-[#DC2626]" />
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#DC2626]">
          Relegation
        </p>
        <p className="text-xs text-[#0A0A0A]/75 mt-1 leading-relaxed">
          Bottom three clubs are relegated to the National Super League (NSL).
        </p>
      </div>
    </div>
  </div>
);

const SkeletonRows = () => (
  <div className="space-y-3" data-testid="league-table-loading">
    {Array.from({ length: 10 }).map((_, idx) => (
      <div key={idx} className="h-12 w-full animate-pulse bg-[#0A0A0A]/5 rounded" />
    ))}
  </div>
);

export const LeagueTablePage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadTable = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const url = `${API}/league-table${isRefresh ? "?force=true" : ""}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error(err);
      setError("We couldn't load the live league table right now. Please try again in a moment.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTable(false);
  }, []);

  const lastUpdated = useMemo(() => formatStamp(data?.last_updated), [data]);
  const fetchedAt = useMemo(() => formatStamp(data?.fetched_at), [data]);

  return (
    <div className="App bg-[#FAFAFA] min-h-screen" data-testid="league-table-page">
      <Navigation />

      {/* Hero / editorial intro */}
      <section className="pt-32 pb-12 px-6 bg-white border-b border-[#E4E4E7]" data-testid="league-table-hero">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.32em] text-[#D1111A] mb-6">
            <Trophy size={14} />
            <span>Sportpesa Premier League</span>
          </div>
          <h1
            className="font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="league-table-title"
          >
            Live League Table
          </h1>
          <p className="mt-6 max-w-2xl text-base text-[#0A0A0A]/70 leading-relaxed">
            The current standings of Kenya's top-flight football clubs, syncing live from the
            Football Kenya Federation. Watch the title race, mid-table battle and relegation
            scrap as the season unfolds.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/55">
            {data?.season && (
              <span data-testid="league-table-season">
                <span className="text-[#0A0A0A]/40 mr-2">Season</span>
                <span className="text-[#0A0A0A] font-bold">{data.season}</span>
              </span>
            )}
            {lastUpdated && (
              <span data-testid="league-table-last-updated">
                <span className="text-[#0A0A0A]/40 mr-2">Last update</span>
                <span className="text-[#0A0A0A] font-bold normal-case tracking-normal">{lastUpdated}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => loadTable(true)}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#0A0A0A]/15 hover:border-[#D1111A] hover:text-[#D1111A] transition-colors disabled:opacity-50"
              data-testid="league-table-refresh-button"
            >
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing" : "Refresh"}
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="border border-[#E4E4E7] bg-white p-4 sm:p-8 shadow-sm">
            {loading && <SkeletonRows />}

            {!loading && error && (
              <div
                className="flex flex-col items-start gap-4 p-6 border-l-4 border-[#D1111A] bg-[#D1111A]/5"
                data-testid="league-table-error"
              >
                <div className="flex items-center gap-2 text-[#D1111A] font-bold uppercase tracking-[0.18em] text-xs">
                  <AlertTriangle size={14} /> Live data unavailable
                </div>
                <p className="text-sm text-[#0A0A0A]/75">{error}</p>
                <button
                  type="button"
                  onClick={() => loadTable(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#D1111A] transition-colors"
                  data-testid="league-table-retry-button"
                >
                  Try again
                </button>
              </div>
            )}

            {!loading && !error && data?.standings?.length > 0 && (
              <>
                <StandingsTable rows={data.standings} />
                <ZoneLegend />
              </>
            )}

            {!loading && !error && data?.standings?.length === 0 && (
              <p className="text-center py-8 text-[#0A0A0A]/55" data-testid="league-table-empty">
                No standings published yet for this season.
              </p>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/55">
            <span data-testid="league-table-source">
              Source ·{" "}
              <a
                href={FKF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#0A0A0A] hover:text-[#D1111A] transition-colors normal-case tracking-normal"
                data-testid="league-table-source-link"
              >
                Football Kenya Federation
                <ArrowUpRight size={12} />
              </a>
            </span>
            {fetchedAt && (
              <span data-testid="league-table-fetched-at" className="normal-case tracking-normal">
                Fetched {fetchedAt}
              </span>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LeagueTablePage;
